import React from 'react';

import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  useTheme,
} from '@mui/material';
import _ from 'es-toolkit/compat';

import { SxProp } from '@hrlogics/react-helper-types';

import { DEFAULT_ID } from '@clearverify/constants';

import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { DEMO_PRIVACY_AUTOCOMPLETE_MIN_SEARCH_LENGTH } from 'src/constants/demoPrivacyMode';
import { useDemoPrivacyBlurred, useDemoPrivacyInputSlotProps } from 'src/hooks/demoPrivacyMode';
import { getFieldError, hasFieldError } from 'src/utils/finalForm';

import type { AutocompleteFieldChipProps, AutocompleteFieldProps } from './interface';

// When you will use initialValues add initialValuesEqual={() => true} property to Form component
// <Form initialValue={initialValues} initialValuesEqual={() => true} />

export const AutocompleteField: React.FC<AutocompleteFieldProps<any>> = ({
  input,
  meta,
  label,
  size,
  helperText,
  options = [],
  optionKey,
  onSearch,
  placeholder = 'Type to search',
  chipProps,
  fullWidth = false,
  disabled = false,
  required = false,
  loading = false,
  freeSolo = false,
  multipleMode,
  unfilterOptions,
  entityName = 'options',
  getOptionLabel,
  renderOption: renderOptionProp,
  demoPrivacyMinSearchLength = DEMO_PRIVACY_AUTOCOMPLETE_MIN_SEARCH_LENGTH,
  ...restFieldProps
}) => {
  const theme = useTheme();
  const { name, value: fieldValue, onBlur, onChange: onChangeField, multiple = false } = input;
  const [value, setValue] = React.useState<unknown>(fieldValue || (multiple ? [] : null));
  const [inputValue, setInputValue] = React.useState<string>('');
  const isAutocompleteSearchActive = Boolean(
    inputValue && inputValue.length >= demoPrivacyMinSearchLength,
  );
  const isAutocompletePrivacyBlurred = useDemoPrivacyBlurred(isAutocompleteSearchActive);
  const demoPrivacyInputSlotProps = useDemoPrivacyInputSlotProps();
  const isAllOptionsSelected = _.isArray(value) ? value.length === options.length : false;

  const [error, errorText] = React.useMemo(() => {
    return [hasFieldError(meta), getFieldError(meta)];
  }, [meta]);

  const mergedChipProps = React.useMemo(() => {
    const defaultChipProps: AutocompleteFieldChipProps = {
      sx: {
        borderRadius: '4px',
        backgroundColor: '#ebefed',
        color: theme.palette.primary.main,
        fontSize: `${theme.typography.fontSize - 2}px`,
      },
      size: 'small',
    };

    return _.merge({}, defaultChipProps, chipProps);
  }, [chipProps, theme]);

  React.useEffect(() => {
    if (!fieldValue) {
      setInputValue('');
      setValue(multiple ? [] : null);
      onChangeField(multiple ? [] : null);
    } else {
      setValue(fieldValue);
    }
  }, [multiple, fieldValue, onChangeField]);

  const onChangeValue = React.useCallback(
    (_event: React.SyntheticEvent, newValueRaw: unknown) => {
      // We can't know the exact type of value
      let newValue: any | any[] = newValueRaw;
      const prevValue: any | any[] = value;

      if (multiple && multipleMode === 'all_option') {
        const isAllOptionSelected = newValue.some((option: any) => option.id === DEFAULT_ID);
        const isAllOptionSelectedInPrev = prevValue.some((option: any) => option.id === DEFAULT_ID);

        if (isAllOptionSelected && isAllOptionSelectedInPrev) {
          newValue = newValue.filter((option: any) => option.id !== DEFAULT_ID);
        } else if (isAllOptionSelected) {
          newValue = [defaultSelectAllOption];
        }

        if (newValue.length === 0) {
          newValue = [defaultSelectAllOption];
        }
      }

      setValue(newValue);
      onChangeField(newValue);
    },
    [multiple, multipleMode, onChangeField, value],
  );

  const onInputChange = React.useCallback(
    (_event: React.SyntheticEvent, inputValue: string) => {
      setInputValue(inputValue);
      onSearch?.(inputValue);
    },
    [onSearch],
  );

  const onSelectAllOptions = React.useCallback(
    (_event: React.SyntheticEvent) => {
      setInputValue('');
      setValue(options);
      onChangeField(options);
    },
    [onChangeField, options],
  );

  const optionsProp = React.useMemo(() => {
    if (multiple && options.length !== 0 && multipleMode === 'all_option') {
      return [defaultSelectAllOption, ...options];
    }

    return options;
  }, [multiple, multipleMode, options]);

  const getOptionLabelProp = React.useMemo(() => {
    if (!getOptionLabel) return undefined;
    // We can't know the exact type of option
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (option: any) => {
      if (option.id === DEFAULT_ID) return `All ${entityName}`;
      return getOptionLabel(option);
    };
  }, [entityName, getOptionLabel]);

  const renderOption = React.useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key: string },
      option: any,
      state: any,
      ownerState: any,
    ) => {
      if (renderOptionProp) {
        return renderOptionProp(props, option, state, ownerState);
      }

      const label = getDisplayLabel(option, getOptionLabelProp);

      return (
        <li {...props} key={String(option.id ?? props.key)}>
          <DemoPrivacyText reveal={isAutocompleteSearchActive}>{label}</DemoPrivacyText>
        </li>
      );
    },
    [getOptionLabelProp, isAutocompleteSearchActive, renderOptionProp],
  );

  const renderTags = React.useCallback(
    (
      tagValue: Array<Record<string, unknown>>,
      getTagProps: (params: { index: number }) => object,
    ) =>
      tagValue.map((option, index) => {
        const label = getDisplayLabel(option, getOptionLabelProp);

        return (
          <Chip
            {...getTagProps({ index })}
            key={String(option.id ?? index)}
            label={<DemoPrivacyText reveal={isAutocompleteSearchActive}>{label}</DemoPrivacyText>}
            {...mergedChipProps}
          />
        );
      }),
    [getOptionLabelProp, isAutocompleteSearchActive, mergedChipProps],
  );

  return (
    <FormControl variant="outlined" error={error} fullWidth={fullWidth} required={required}>
      <Autocomplete
        multiple={multiple}
        freeSolo={freeSolo}
        options={optionsProp}
        onBlur={onBlur}
        // TODO: incorrect typecheck error here
        value={value as any}
        onChange={onChangeValue}
        inputValue={inputValue}
        onInputChange={onInputChange}
        disabled={disabled}
        slotProps={{ chip: mergedChipProps }}
        filterSelectedOptions
        filterOptions={unfilterOptions ? options => options : undefined}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionKey={optionKey ? getOptionKey(optionKey) : undefined}
        noOptionsText={loading ? 'Loading...' : undefined}
        renderInput={params => (
          <TextField
            name={name}
            {...params}
            label={label}
            required={required}
            placeholder={placeholder}
            variant="outlined"
            error={Boolean(errorText?.length)}
            slotProps={{
              input: {
                size,
                ...params.InputProps,
                sx: isAutocompletePrivacyBlurred ? demoPrivacyInputSlotProps?.input?.sx : undefined,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="secondary" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              },
            }}
            helperText={
              <React.Fragment>
                {helperText && (
                  <FormHelperText error={false} sx={{ margin: 0 }}>
                    {helperText}
                  </FormHelperText>
                )}

                {multiple && options.length !== 0 && multipleMode === 'select_all' && (
                  <Button
                    variant="text"
                    sx={selectAllButtonSxProp}
                    onClick={onSelectAllOptions}
                    disabled={isAllOptionsSelected || loading || disabled}
                  >
                    Select all {entityName}
                  </Button>
                )}

                {errorText && (
                  <FormHelperText error sx={{ margin: 0 }}>
                    {errorText}
                  </FormHelperText>
                )}
              </React.Fragment>
            }
          />
        )}
        getOptionLabel={getOptionLabelProp}
        renderOption={renderOption}
        renderTags={multiple ? renderTags : undefined}
        {...restFieldProps}
      />
    </FormControl>
  );
};

// Mirrors MUI's default getOptionLabel (option.label ?? option) so options and tags
// don't render blank when no getOptionLabel prop is passed
const getDisplayLabel = (option: any, getOptionLabel?: (option: any) => string): string => {
  if (getOptionLabel) return getOptionLabel(option);
  return _.isString(option) ? option : String(option?.label ?? '');
};

const isOptionEqualToValue = (option: Record<string, string>, value: Record<string, string>) => {
  if (_.has(option, 'id') && _.has(value, 'id')) {
    return option.id === value.id;
  }

  if (_.has(option, 'value') && _.isString(value)) {
    return option.value === value;
  }

  return _.isEqual(option, value);
};

const getOptionKey = (optionKeyProp: string | number | symbol) => {
  return (option: string | Record<string, any>) => {
    return _.isString(option) ? option : option[optionKeyProp.toString()].toString() || '';
  };
};

const defaultSelectAllOption = { id: DEFAULT_ID };

const selectAllButtonSxProp: SxProp = {
  fontSize: '14px',
  textTransform: 'none',
  padding: '0',
  marginTop: '-3px',
  '&:hover': {
    backgroundColor: 'transparent',
  },
};
